import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { useAutoNavigate } from '@betterment/js-runtime';
import { Box, Dialog, H4, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { ErrorBoundary, ListRowLoader, useFeatureFlag } from '@betterment/shared-components';

import { ProductRow } from './components/ProductRow';
import { ProductSelectionOptionsDocument } from './graphql/productSelectionOptions.generated';
import { useDisclosuresUpdate } from './hooks/useDisclosuresUpdate';

import StepHeading from '../../../AccountCreationFlow/components/StepHeading';
import { trackClick } from '../../../features/track';
import { AccountRecommendationModal } from '../../../home/account-recommendation-modal/AccountRecommendationModal';
import { RoundedBorderBox } from '../../../home/account-recommendation-modal/components/RoundedBorderBox';
import { AccountRecommendationOptionDocument } from '../../../home/account-recommendation-modal/graphql/query.accountRecommendationOption.generated';
import { WorkflowLayout } from '../../../Shared/layout/WorkflowLayout';
import { urlBuilder } from '../../../Shared/urlBuilder';

const Content: React.FC = () => {
  const { enabled: isAccountRecommendationEnabled } = useFeatureFlag({
    splitName: 'retail.ai_account_recommendation_q1_2026_enabled',
    context: 'ProductOnboardingCrossSellCard',
  });
  const { data: accountRecommendationOptionData } = useGraphQLQuery(AccountRecommendationOptionDocument, {
    throwOnError: false,
    skip: !isAccountRecommendationEnabled,
  });
  const { data } = useGraphQLQuery(ProductSelectionOptionsDocument, { throwOnError: true });

  const accountRecommendationOption = accountRecommendationOptionData?.accountRecommendationOption;
  const options = data?.productOnboardingOptions;
  useDisclosuresUpdate(options);

  if (!options) {
    return (
      <>
        <Separator />
        <ListRowLoader />
        <Separator />
        <ListRowLoader />
        <Separator />
        <ListRowLoader />
        <Separator />
        <ListRowLoader />
        <Separator />
        <ListRowLoader />
      </>
    );
  }
  return (
    <>
      {accountRecommendationOption && (
        <AccountRecommendationModal>
          <Box css={{ paddingBottom: 'space.300' }}>
            <Dialog.Trigger asChild={true}>
              <RoundedBorderBox
                borderRadius={4}
                role="button"
                tabIndex={0}
                style={{ width: '100%' }}
                onClick={() =>
                  trackClick({
                    name: 'GetAccountRecommendation',
                    flow: 'AddGoal',
                    module: 'ProductCategory',
                  })
                }
              >
                <Box className="group" css={{ cursor: 'pointer' }}>
                  <ListRow>
                    <ListRow.LeadingAsset>
                      <Icon name="Guidance" color="icon.accent.gold" size="small" />
                    </ListRow.LeadingAsset>
                    <ListRow.LeadingContent>
                      <H4
                        variant="title100"
                        color="text"
                        css={{ textDecoration: { _groupHover: 'underline', _groupFocus: 'underline' } }}
                      >
                        Get an account recommendation
                      </H4>
                      <Text variant="text50" color="text.weak">
                        Based on your financial profile and accounts
                      </Text>
                    </ListRow.LeadingContent>
                    <ListRow.TrailingAsset>
                      <Icon name="CaretRight" size="small" color="icon" />
                    </ListRow.TrailingAsset>
                  </ListRow>
                </Box>
              </RoundedBorderBox>
            </Dialog.Trigger>
          </Box>
        </AccountRecommendationModal>
      )}
      {options.map(option => (
        <React.Fragment key={option.id}>
          <Separator />
          <ProductRow option={option} />
        </React.Fragment>
      ))}
    </>
  );
};

export const ProductSelectionStep: React.FC = () => {
  const navigate = useAutoNavigate();

  return (
    <WorkflowLayout
      feature="add_account"
      pageTitle="What account do you want to open?"
      workflowTitle=""
      onClose={() => navigate(urlBuilder.appHome.absolute())}
    >
      <Box css={{ marginTop: 'space.400', marginBottom: 'space.600' }}>
        <StepHeading>What account do you want to open?</StepHeading>
      </Box>

      <ErrorBoundary>
        <Content />
      </ErrorBoundary>
    </WorkflowLayout>
  );
};
